if (!customElements.get('quick-add-modal')) {
  // Product page HTML per URL, shared by every quick-add modal, so reopening (or opening after a hover prefetch) skips the network
  const quickAddCache = new Map();
  const QUICK_ADD_CACHE_TTL = 5 * 60 * 1000;
  const fetchQuickAddProduct = (url) => {
    const cached = quickAddCache.get(url);
    if (cached && Date.now() - cached.time < QUICK_ADD_CACHE_TTL) return cached.promise;
    const promise = fetch(url).then((response) => {
      if (!response.ok) throw new Error(response.status);
      return response.text();
    });
    quickAddCache.set(url, { promise, time: Date.now() });
    promise.catch(() => quickAddCache.delete(url));
    return promise;
  };

  ['pointerover', 'touchstart', 'focusin'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const opener = event.target.closest && event.target.closest('modal-opener [data-product-url]');
        if (opener) fetchQuickAddProduct(opener.getAttribute('data-product-url')).catch(() => {});
      },
      { passive: true }
    );
  });

  // Theme scripts in the product section only define guarded custom elements, so once loaded they never need to run again
  const GUARDED_THEME_SCRIPT = /\/assets\/(product-info|product-form|share|pickup-availability|product-modal|media-gallery)\.js/;
  const loadedThemeScripts = new Set();
  // Scripts inside `container` are the freshly injected, not-yet-run copies, so they don't count as loaded
  const isLoadedThemeScript = (src, container) =>
    GUARDED_THEME_SCRIPT.test(src) &&
    (loadedThemeScripts.has(src) ||
      Array.from(document.scripts).some((script) => !container.contains(script) && script.getAttribute('src') === src));

  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetchQuickAddProduct(opener.getAttribute('data-product-url'))
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
            this.productElement.classList.forEach((classApplied) => {
              if (classApplied.startsWith('color-') || classApplied === 'gradient')
                this.modalContent.classList.add(classApplied);
            });
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const src = oldScriptTag.getAttribute('src');
          if (src && isLoadedThemeScript(src, element)) {
            oldScriptTag.remove();
            return;
          }
          if (src && GUARDED_THEME_SCRIPT.test(src)) loadedThemeScripts.add(src);
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      preventVariantURLSwitching() {
        const variantPicker = this.modalContent.querySelector('variant-radios,variant-selects');
        if (!variantPicker) return;

        variantPicker.setAttribute('data-update-url', 'false');
      }

      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = this.productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${sectionId}`);
        this.productElement.querySelectorAll('variant-selects, variant-radios, product-info').forEach((element) => {
          element.dataset.originalSection = sectionId;
        });
      }

      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 768px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
